/* =========================================================
   Heatronics - one product-card rail, everywhere.
   Pairs with assets/card-rails.css.

   WHAT IT DOES
   1. Takes every product carousel off slick and turns it into
      a native horizontal scroll rail with snap points, at
      every width. Same behaviour on a phone and on a desktop -
      only the card width differs.
   2. Merges the five tab panels of the hCore section into ONE
      continuous rail in tab order, so you scroll straight from
      hCore X into Neck, Knee, Foot Warmer and Bed Warmer. The
      pills become a scroll-spy - they follow the rail, and
      tapping one jumps to that group.
   3. Draws a progress bar under each rail, and sets data-at on
      the rail so the CSS can fade whichever edge still has
      cards behind it.
   4. Gives a mouse the same gesture a finger gets: press and
      drag the rail sideways. A drag past 5px swallows the
      click that follows, so dragging never opens a product.

   WHY NATIVE SCROLLING RATHER THAN SLICK
   Three attempts to make slick show the right number of cards
   all failed the same way. A fractional slidesToShow makes
   slick compute the narrower slide but never rewrite the track
   transform, so every card lands out of position; setPosition,
   slickGoTo and a synthetic resize all left the stale
   transform. Laying the track out as a CSS grid ran into
   slick's inline slide widths AND its clearfix pseudo-
   elements, which become grid items and steal two cells. A
   native rail has none of these problems, gets real momentum
   scrolling, and needs no clones.

   THE BUG THAT FROZE THE PAGE, AND WHY IT IS STRUCTURED THIS
   WAY NOW. The sections initialise slick after this script,
   and some do it twice, so a MutationObserver watches each
   carousel's class attribute and re-takes it if `slick-
   initialized` comes back. The first version called
   classList.remove INSIDE that callback - which mutates the
   very attribute being observed, firing the callback again,
   forever. The page locked solid.

   Two rules here as a result, and they are worth keeping:
     - the observer is DISCONNECTED around any class change we
       make ourselves (see mute()), so we can never hear our
       own edits;
     - and a retake budget stops it even if something else is
       fighting us for the same element.
   ========================================================= */
(function () {
  var MAX_RETAKES = 6;

  function $j() { return window.jQuery; }
  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Run a class change with the element's observer detached, so
     our own edit can never re-enter the callback. */
  function mute(el, fn) {
    var obs = el.hbObs;
    if (obs) obs.disconnect();
    try { fn(); } finally {
      if (obs && el.hbObsOpts) obs.observe(el, el.hbObsOpts);
    }
  }

  /* ---------- slick removal ----------------------------- */

  function unslick(el) {
    if (!el.classList.contains('slick-initialized')) return true;
    mute(el, function () { el.classList.remove('hb-rail'); });
    var jq = $j();
    if (!jq || !jq.fn || !jq.fn.slick) return false;
    try { jq(el).slick('unslick'); } catch (e) {}
    return !el.classList.contains('slick-initialized');
  }

  function markRail(el) {
    mute(el, function () { el.classList.add('hb-rail'); });
  }

  function guard(el, onRetake) {
    if (el.hbObs || !window.MutationObserver) return;
    el.hbRetakes = 0;
    var pending = null;
    var obs = new window.MutationObserver(function () {
      if (!el.classList.contains('slick-initialized')) return;
      if (el.hbRetakes >= MAX_RETAKES) { obs.disconnect(); return; }
      mute(el, function () { el.classList.remove('hb-rail'); });
      window.clearTimeout(pending);
      pending = window.setTimeout(function () {
        el.hbRetakes++;
        if (unslick(el)) { markRail(el); onRetake(); }
      }, 80);
    });
    el.hbObs = obs;
    el.hbObsOpts = { attributes: true, attributeFilter: ['class'] };
    obs.observe(el, el.hbObsOpts);
  }

  /* ---------- edge fades ---------------------------------
     data-at says which side still has cards off-screen, so the
     CSS only hazes the edge that has something behind it. */

  function paintEdges(el) {
    var max = el.scrollWidth - el.clientWidth;
    if (max < 4) { el.removeAttribute('data-at'); return; }
    if (el.scrollLeft <= 2) el.dataset.at = 'start';
    else if (el.scrollLeft >= max - 2) el.dataset.at = 'end';
    else el.dataset.at = 'middle';
  }

  /* ---------- progress bar ------------------------------ */

  function progressFor(rail) {
    var host = rail.parentNode;
    if (!host) return null;
    if (rail.hbBar && rail.hbBar.parentNode === host) return rail.hbBar;
    var bar = host.querySelector(':scope > .hb-prog');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'hb-prog';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span class="hb-prog__bar"></span>';
      host.appendChild(bar);
    }
    rail.hbBar = bar;
    return bar;
  }

  function paintProgress(rail) {
    paintEdges(rail);
    var bar = progressFor(rail);
    if (!bar) return;
    var inner = bar.firstElementChild;
    var max = rail.scrollWidth - rail.clientWidth;
    if (max < 4 || !rail.classList.contains('hb-rail')) { bar.hidden = true; return; }
    bar.hidden = false;
    var frac = rail.clientWidth / rail.scrollWidth;
    var w = Math.max(frac * 100, 12);
    var travel = bar.clientWidth * (1 - w / 100);
    var x = Math.max(0, Math.min(rail.scrollLeft / max, 1)) * travel;
    inner.style.width = w + '%';
    inner.style.transform = 'translateX(' + x + 'px)';
  }

  // scroll events are throttled or dropped during momentum, so
  // poll while a gesture is in flight
  function follow(rail, fn) {
    if (rail.hbFollowing) { rail.hbUntil = Date.now() + 900; return; }
    rail.hbFollowing = true;
    rail.hbUntil = Date.now() + 900;
    var last = -1;
    var frames = 0;
    (function frame() {
      frames++;
      if (rail.scrollLeft !== last) {
        last = rail.scrollLeft;
        fn();
        rail.hbUntil = Math.max(rail.hbUntil, Date.now() + 300);
      }
      if (Date.now() < rail.hbUntil && frames < 600) {
        window.requestAnimationFrame(frame);
      } else {
        rail.hbFollowing = false;
        fn();
      }
    })();
  }

  /* ---------- press and drag, for a mouse ----------------
     A finger gets momentum scrolling for free; a mouse gets
     nothing, because a vertical wheel does not move a
     horizontal rail. So the rail is draggable. The 5px
     threshold is what separates a drag from a click on a
     card, and hbSuppressClick swallows the click that the
     browser fires at the end of a drag. */

  function dragScroll(el) {
    if (el.hbDrag) return;
    el.hbDrag = true;
    var down = false, moved = false, startX = 0, startLeft = 0;

    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true; moved = false;
      startX = e.clientX; startLeft = el.scrollLeft;
    });

    el.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < 5) return;
        moved = true;
        el.classList.add('hb-dragging');
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
      }
      el.scrollLeft = startLeft - dx;
      paintProgress(el);
      e.preventDefault();
    });

    function end() {
      if (!down) return;
      down = false;
      if (moved) {
        el.classList.remove('hb-dragging');
        el.hbSuppressClick = Date.now() + 250;
        paintProgress(el);
      }
    }
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('pointerleave', end);

    el.addEventListener('click', function (e) {
      if (el.hbSuppressClick && Date.now() < el.hbSuppressClick) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  function wire(rail, onScroll) {
    if (rail.hbWired) { onScroll(); return; }
    rail.hbWired = true;
    rail.addEventListener('scroll', function () {
      window.requestAnimationFrame(onScroll);
    }, { passive: true });
    rail.addEventListener('touchstart', function () { follow(rail, onScroll); }, { passive: true });
    rail.addEventListener('pointerdown', function () { follow(rail, onScroll); }, { passive: true });
    rail.addEventListener('wheel', function () { follow(rail, onScroll); }, { passive: true });
    dragScroll(rail);
    onScroll();
  }

  /* ---------- a scroll tween that always works ----------
     scrollTo({behavior:'smooth'}) on an overflow container is
     not reliably honoured - it silently did nothing in
     testing - so rail and pill movement is tweened on rAF. */

  function tweenScroll(el, to, onFrame) {
    var from = el.scrollLeft;
    var delta = to - from;
    if (Math.abs(delta) < 1) { if (onFrame) onFrame(); return; }
    if (reducedMotion()) {
      el.scrollLeft = to;
      if (onFrame) onFrame();
      return;
    }
    var DUR = 420;
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var t = Math.min((ts - start) / DUR, 1);
      var e = 1 - Math.pow(1 - t, 3);
      el.scrollLeft = from + delta * e;
      if (onFrame) onFrame();
      if (t < 1) window.requestAnimationFrame(frame);
    }
    window.requestAnimationFrame(frame);
  }

  /* ---------- plain rails (the landers) ----------------- */

  function makeRail(el) {
    if (!unslick(el)) return false;
    markRail(el);
    guard(el, function () { wire(el, function () { paintProgress(el); }); });
    wire(el, function () { paintProgress(el); });
    return true;
  }

  function sweepPlainRails(scope) {
    var done = true;
    var els = (scope || document).querySelectorAll('.cf-slick');
    for (var i = 0; i < els.length; i++) {
      if (!makeRail(els[i])) done = false;
    }
    return done;
  }

  /* ---------- the hCore tab section --------------------- */

  function centrePill(nav, li) {
    var navRect = nav.getBoundingClientRect();
    var liRect = li.getBoundingClientRect();
    var within = liRect.left - navRect.left + nav.scrollLeft;
    var target = within - (nav.clientWidth - liRect.width) / 2;
    var max = nav.scrollWidth - nav.clientWidth;
    tweenScroll(nav, Math.max(0, Math.min(target, max)), function () { paintEdges(nav); });
  }

  function mergeTabs(section) {
    if (section.hbMerged) return true;

    var panels = section.querySelectorAll('.ht-panel');
    var nav = section.querySelector('.ht-nav');
    var inner = section.querySelector('.ht-inner');
    if (!panels.length || !nav || !inner) return true;

    // every panel's carousel must be off slick before its cards
    // can move, or slick's clones come with them
    var slicks = section.querySelectorAll('.ht-slick');
    for (var s = 0; s < slicks.length; s++) {
      if (!unslick(slicks[s])) return false;
    }

    var wrap = document.createElement('div');
    wrap.className = 'hb-railwrap';
    var rail = document.createElement('div');
    rail.className = 'hb-rail';
    rail.setAttribute('role', 'list');
    wrap.appendChild(rail);

    for (var p = 0; p < panels.length; p++) {
      var cards = panels[p].querySelectorAll('.ht-slick-slide');
      if (!cards.length) cards = panels[p].querySelectorAll('.ht-card-wrapper');
      for (var c = 0; c < cards.length; c++) {
        var card = cards[c];
        if (card.classList.contains('slick-cloned')) continue;
        if (rail.contains(card)) continue;
        card.dataset.hbGroup = String(p);
        card.setAttribute('role', 'listitem');
        rail.appendChild(card);
      }
    }

    if (!rail.children.length) return true;

    inner.appendChild(wrap);
    section.classList.add('hb-merged');
    section.hbMerged = true;

    var links = nav.querySelectorAll('.ht-nav-link');

    function groupStarts() {
      var starts = [];
      var kids = rail.children;
      if (!kids.length) return starts;
      var base = kids[0].offsetLeft;
      for (var i = 0; i < kids.length; i++) {
        var g = +kids[i].dataset.hbGroup;
        if (starts[g] === undefined) starts[g] = kids[i].offsetLeft - base;
      }
      return starts;
    }

    function lastGroup(starts) {
      for (var k = starts.length - 1; k >= 0; k--) {
        if (starts[k] !== undefined) return k;
      }
      return 0;
    }

    function setActive(g) {
      for (var i = 0; i < links.length; i++) {
        var on = (i === g);
        links[i].classList.toggle('hb-on', on);
        links[i].classList.toggle('ht-nav-link--active', on);
        links[i].setAttribute('aria-selected', on ? 'true' : 'false');
      }
    }

    var current = -1;
    function spy() {
      paintProgress(rail);
      /* A tapped pill holds the highlight briefly. Without this
         the last group's pill could never stay lit: its cards
         are already on screen when the rail hits its right end,
         so the tween stops short of that group's start and the
         spy immediately re-lights an earlier pill. */
      if (rail.hbLock && Date.now() < rail.hbLock) return;

      var starts = groupStarts();
      var max = rail.scrollWidth - rail.clientWidth;
      var g;
      if (max > 4 && rail.scrollLeft >= max - 2) {
        g = lastGroup(starts);
      } else {
        var x = rail.scrollLeft + 40;
        g = 0;
        for (var i = 0; i < starts.length; i++) {
          if (starts[i] !== undefined && starts[i] <= x) g = i;
        }
      }
      if (g === current) return;
      current = g;
      setActive(g);
      var li = links[g] && links[g].parentNode;
      if (li) centrePill(nav, li);
    }

    wire(rail, spy);

    for (var l = 0; l < links.length; l++) {
      (function (idx, link) {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var starts = groupStarts();
          if (starts[idx] === undefined) return;
          var max = rail.scrollWidth - rail.clientWidth;
          rail.hbLock = Date.now() + 900;
          tweenScroll(rail, Math.min(starts[idx], max), function () { paintProgress(rail); });
          setActive(idx);
          current = idx;
        }, true);
      })(l, links[l]);
    }

    nav.addEventListener('scroll', function () {
      window.requestAnimationFrame(function () { paintEdges(nav); });
    }, { passive: true });

    paintEdges(nav);
    spy();
    return true;
  }

  function sweepTabSections(scope) {
    var done = true;
    var navs = (scope || document).querySelectorAll('.ht-nav');
    for (var i = 0; i < navs.length; i++) {
      var section = navs[i].closest ? navs[i].closest('[id^="shopify-section"]') : null;
      if (section && !mergeTabs(section)) done = false;
    }
    return done;
  }

  /* ---------- wiring ------------------------------------ */

  function sweep(scope) {
    var a = sweepPlainRails(scope);
    var b = sweepTabSections(scope);
    return a && b;
  }

  function boot() {
    var delays = [250, 600, 1200, 2400, 4000, 6000];
    sweep();
    for (var i = 0; i < delays.length; i++) {
      window.setTimeout(function () { sweep(); }, delays[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  var t;
  window.addEventListener('resize', function () {
    window.clearTimeout(t);
    t = window.setTimeout(function () {
      var rails = document.querySelectorAll('.hb-rail');
      for (var i = 0; i < rails.length; i++) paintProgress(rails[i]);
      var navs = document.querySelectorAll('.hb-merged .ht-nav');
      for (var n = 0; n < navs.length; n++) paintEdges(navs[n]);
    }, 200);
  });

  document.addEventListener('shopify:section:load', function (e) {
    window.setTimeout(function () { sweep(e.target); }, 250);
  });
})();